
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"

interface StudentPaginationProps {
  page: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

export function StudentPagination({
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
}: StudentPaginationProps) {
  // Calculate which page buttons to show
  const getPageNumbers = () => {
    if (totalPages <= 5) {
      // If 5 or fewer pages, show all
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      // Otherwise, show a window around the current page
      const pageNumbers = [];
      
      // Always show first page
      pageNumbers.push(1);
      
      // Calculate the window
      const startPage = Math.max(2, page - 1);
      const endPage = Math.min(totalPages - 1, page + 1);
      
      // Add ellipsis if needed
      if (startPage > 2) {
        pageNumbers.push('ellipsis-start');
      }
      
      // Add pages in the window
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push('ellipsis-end');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pageNumbers.push(totalPages);
      }
      
      return pageNumbers;
    }
  };

  const pageNumbers = getPageNumbers();
  
  // Calculate display range
  const startItem = Math.min(totalItems, ((page - 1) * pageSize) + 1);
  const endItem = Math.min(page * pageSize, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="text-sm text-muted-foreground order-2 sm:order-1">
        {totalItems > 0 ? (
          <>Showing {startItem} to {endItem} of {totalItems} students</>
        ) : (
          <>No students found</>
        )}
      </div>
      <div className="flex items-center gap-1 order-1 sm:order-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="h-7 w-7"
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        <div className="flex items-center gap-1">
          {pageNumbers.map((pageNum, i) => {
            if (pageNum === 'ellipsis-start' || pageNum === 'ellipsis-end') {
              return <span key={`ellipsis-${i}`} className="mx-1">...</span>;
            }
            
            return (
              <Button
                key={`page-${pageNum}`}
                variant={pageNum === page ? "default" : "outline"}
                size="icon"
                onClick={() => onPageChange(Number(pageNum))}
                className="h-7 w-7"
                aria-label={`Go to page ${pageNum}`}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>
        
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages || totalPages === 0}
          className="h-7 w-7"
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
